import React from 'react';
import {
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Box,
  alpha,
} from '@mui/material';
import {
  Warning as ForceIcon,
  History as HistoryIcon,
  Inbox as EmptyIcon,
} from '@mui/icons-material';
import type { AppRelease, ReleaseStatus } from '../../types/AppMobileTypes';

interface ReleaseHistoryTableProps {
  releases: AppRelease[];
  loading?: boolean;
}

function formatDate(timestamp: any): string {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusChip(status: ReleaseStatus) {
  switch (status) {
    case 'published':
      return <Chip label="Attiva" size="small" color="success" sx={{ height: 22, fontSize: 11 }} />;
    case 'superseded':
      return <Chip label="Sostituita" size="small" sx={{ height: 22, fontSize: 11, bgcolor: alpha('#000', 0.06), color: 'text.secondary' }} />;
    case 'draft':
      return <Chip label="Bozza" size="small" color="warning" sx={{ height: 22, fontSize: 11 }} />;
    default:
      return <Chip label={status} size="small" sx={{ height: 22, fontSize: 11 }} />;
  }
}

export const ReleaseHistoryTable: React.FC<ReleaseHistoryTableProps> = ({
  releases,
  loading,
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: alpha('#1b1b1b', 0.03),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <HistoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ letterSpacing: '0.02em' }}>
          Storico rilasci
        </Typography>
        {!loading && releases.length > 0 && (
          <Chip
            label={releases.length}
            size="small"
            sx={{ height: 20, fontSize: 10, minWidth: 20, bgcolor: alpha('#000', 0.06) }}
          />
        )}
      </Box>

      <Box sx={{ p: loading || releases.length === 0 ? 3 : 0 }}>
        {loading ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : releases.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: alpha('#78909c', 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <EmptyIcon sx={{ fontSize: 24, color: 'grey.400' }} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Nessun rilascio nello storico
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Le versioni pubblicate appariranno qui
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', fontWeight: 600 }}>
                    Versione
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', fontWeight: 600 }}>
                    Data
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', fontWeight: 600 }}>
                    Stato
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', fontWeight: 600, width: 30 }}>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {releases.map((release) => (
                  <TableRow
                    key={release.versionCode}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {release.versionName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          ({release.versionCode})
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                        {formatDate(release.published_at || release.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(release.status)}
                    </TableCell>
                    <TableCell align="center" sx={{ px: 1 }}>
                      {release.forceUpdate && (
                        <ForceIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Card>
  );
};
